import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Slide,
  Switch,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { TransitionProps } from '@mui/material/transitions'
import { availableTools } from '../model/tools'
import { MedconnectAgentConfig } from '@/core/models/account.model'
import { useGetAvailableToolsQuery } from '@/core/services/medconnect-agent-service'
import type { ToolGroupCatalogEntry } from '@/core/api/medconnect-agent'
import { useChangeToolConfig } from '../model/useChangeToolConfig'

interface ToolConfigurationPanelProps {
  open: boolean
  onClose: () => void
  config: MedconnectAgentConfig
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

/** Static i18n metadata for tools we have labels for; unknown tools fall back to a humanized name. */
const TOOL_LABELS = new Map(availableTools.map((tool) => [tool.name, tool]))

const humanizeToolName = (name: string) => name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())

const humanizeGroupName = (group: string) => group.replace(/[-_]/g, ' ').replace(/^./, (c) => c.toUpperCase())

/**
 * Per-user tool configuration. Groups come from `GET /medconnect-agent/tools/available` (the
 * registry catalog scoped to the current user); toggles persist in `medconnectAgentConfig`.
 */
export function ToolConfigurationPanel({ open, onClose, config }: ToolConfigurationPanelProps) {
  const { t } = useTranslation()
  const changeToolConfig = useChangeToolConfig()
  const { data, isLoading, isError } = useGetAvailableToolsQuery(undefined, { skip: !open })

  const groups: ToolGroupCatalogEntry[] = data?.groups ?? []
  const allToolNames = useMemo(() => data?.allAvailableTools ?? groups.flatMap((g) => g.toolNames), [data, groups])

  const handleToolToggle = (toolName: string, isEnabled: boolean) => {
    const { enabledTools = [], disabledTools = [] } = config

    let newEnabledTools = [...enabledTools]
    let newDisabledTools = [...disabledTools]

    if (isEnabled) {
      newEnabledTools = [...newEnabledTools, toolName]
      newDisabledTools = newDisabledTools.filter((name) => name !== toolName)
    } else {
      newEnabledTools = newEnabledTools.filter((name) => name !== toolName)
      newDisabledTools = [...newDisabledTools, toolName]
    }

    changeToolConfig({
      enabledTools: newEnabledTools,
      disabledTools: newDisabledTools,
    })
  }

  const handleActivateAll = () => {
    changeToolConfig({
      enabledTools: [],
      disabledTools: [],
    })
  }

  const handleDeactivateAll = () => {
    changeToolConfig({
      enabledTools: [],
      disabledTools: allToolNames,
    })
  }

  const isToolEnabled = (toolName: string) => {
    const { enabledTools, disabledTools } = config

    if (disabledTools?.includes(toolName)) {
      return false
    }
    if (enabledTools?.length) {
      return enabledTools.includes(toolName)
    }
    return true
  }

  const renderTool = (toolName: string) => {
    const meta = TOOL_LABELS.get(toolName)
    return (
      <ListItem key={toolName} sx={{ pl: 0, pr: 0 }}>
        <ListItemText
          primary={meta ? t(meta.titleKey) : humanizeToolName(toolName)}
          secondary={meta ? t(meta.descriptionKey) : toolName}
          secondaryTypographyProps={{ style: { whiteSpace: 'pre-line' } }}
        />
        <Switch checked={isToolEnabled(toolName)} onChange={(e) => handleToolToggle(toolName, e.target.checked)} edge="end" />
      </ListItem>
    )
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" color="white" component="div">
            {t('page.medconnectAgent.config.title')}
          </Typography>
          <Button color="primary" onClick={handleActivateAll} disabled={!data}>
            {t('page.medconnectAgent.config.activateAll')}
          </Button>
          <Button color="primary" onClick={handleDeactivateAll} disabled={!data}>
            {t('page.medconnectAgent.config.deactivateAll')}
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('page.medconnectAgent.config.description')}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2">{t('page.medconnectAgent.config.loading')}</Typography>
          </Box>
        )}

        {isError && <Alert severity="error">{t('page.medconnectAgent.config.loadError')}</Alert>}

        {!isLoading && !isError && groups.length === 0 && (
          <Alert severity="info">{t('page.medconnectAgent.config.noTools')}</Alert>
        )}

        {groups.map((group) => {
          const enabledCount = group.toolNames.filter(isToolEnabled).length
          return (
            <Accordion key={group.group} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1, pr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">{humanizeGroupName(group.group)}</Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('page.medconnectAgent.config.enabledCount', { enabled: enabledCount, total: group.toolNames.length })}
                    />
                  </Box>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary">
                      {group.description}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>{group.toolNames.map(renderTool)}</List>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </Dialog>
  )
}

export default ToolConfigurationPanel
